import * as React from 'react';
import {RouteComponentProps} from 'react-router';
import {trackOnce} from '../../../utils/tracker';
import {validateEmail} from '../../../utils/validator';
import {GutterSize} from '../../../common/enums';
import {AuthLabel as Label} from '../label';
import {AuthFooter as Footer} from '../footer';
import {AuthModal as Modal} from '../modal/modal';
import {AuthHeader as Header} from '../header';
import {AuthContent as Content} from '../content';
import {AuthButton as Button} from '../button';
import {ForgotPasswordAlert as Alert} from './alert';
import {Input} from '../../input/input';
import {KEY_ENTER} from '../service';
import {IForgotPasswordConnected} from './types';

interface IForgotPasswordProps extends IForgotPasswordConnected, RouteComponentProps<{}> {
  forgotPassword?: (email: string) => void;
  isCompany?: boolean;
}

interface IForgotPasswordState {
  submitted?: boolean;
  email?: string;
  error?: string;
}

export class ForgotPassword extends React.Component<IForgotPasswordProps, IForgotPasswordState> {
  public state: IForgotPasswordState = {
    submitted: false,
    email: '',
    error: '',
  };

  private emailInput: HTMLElement;

  public componentDidMount(): void {
    this.emailInput && this.emailInput.focus();
    trackOnce(this.props.isCompany ? 'company-forgotpassword' : 'forgotpassword');
  }

  public render(): React.ReactElement<{}> {
    const {error, submitted, email} = this.state;
    const {
      isLoading,
      emailSent,
      apiEmailError,
      connectionRefusedError,
      isCompany,
    } = this.props;

    return (
      <Modal
        label={'Forgot Password'}
        onClose={this.closeModal}
      >
        <Header>Forgot Password</Header>
        <Content>
          {/* @TODO move form to separate Component */}
          <form
            noValidate
            onSubmit={this.onFormSubmit}
          >
            {emailSent && submitted
              ? <Alert email={email} onClick={this.showSendEmailForm} />
              : <div>
                  <Input
                    bottomSpacing={GutterSize.MEDIUM}
                    placeholder={'Email'}
                    reference={node => this.emailInput = node}
                    focused={!submitted || !!error}
                    error={error || apiEmailError}
                    value={email}
                    type={'text'}
                    name={'email'}
                    onKeyDown={this.onKeyDown}
                    onChange={this.changeEmail}
                  />

                  <Button
                    isLoading={isLoading}
                    error={submitted && connectionRefusedError}
                    label={'Send'}
                    type={'submit'}
                  />
                </div>
            }
          </form>
        </Content>

        {!isCompany &&
          <Footer>
            <Label>Don't have an account?</Label>
            <a href="https://www.tryexponent.com/practice?utm_source=pramp&utm_campaign=pramp_forgot_password&utm_medium=referral" className='auth-link auth-link_purple'>Sign up on Exponent</a>
          </Footer>
        }
      </Modal>
    );
  }

  private showSendEmailForm = (e) => {
    e.preventDefault();
    this.setState({submitted: false});
  };

  private onFormSubmit = (e: React.FormEvent<any>): void => {
    e.preventDefault();
    this.sendForm();
  };

  private onKeyDown = (e: React.KeyboardEvent<any>): void => {
    if (e.keyCode === KEY_ENTER) {
      this.sendForm();
    }
  };

  private changeEmail = (email: string): void => {
    this.setState({
      email,
      error: '',
    });
  };

  private sendForm(): void {
    if (this.isFormValid()) {
      this.props.forgotPassword(this.state.email.trim());
      this.setState({submitted: true});
    }
  }

  private isFormValid(): boolean {
    const {email} = this.state;
    const error = validateEmail(email);

    if (error) {
      this.setState({error});
      this.emailInput.focus();
      return false;
    }

    return true;
  }

  private closeModal = (): void => {
    this.props.history.push('/');
  };
}
